import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useGetSubmissionFeedback } from '@onesource/services';
import { FC, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

import { FeedbackNote } from './feedback-note';
import { FEEDBACK_TINT } from './feedback-palette';
import { SubmissionFeedbackSkeleton } from './submission-feedback-skeleton';

interface SubmissionFeedbackProps {
  submissionId: string;
  /** The newest reviewed submission opens with its feedback showing. */
  defaultOpen?: boolean;
}

/**
 * Inside a submission card on a Hands-On Practice form: "View feedback" opens the
 * teacher's note for that submission. Fetched on first open only.
 */
export const SubmissionFeedback: FC<SubmissionFeedbackProps> = ({ submissionId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [hasOpened, setHasOpened] = useState(defaultOpen);
  const { data: feedback, isLoading, isError } = useGetSubmissionFeedback(submissionId, hasOpened);

  const toggle = () => {
    setOpen(prev => !prev);
    setHasOpened(true);
  };

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        onClick={toggle}
        endIcon={open ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
        sx={{
          textTransform: 'none',
          // `&&` outweighs the theme's text-button colour.
          '&&': { color: FEEDBACK_TINT.ink },
          fontWeight: 600,
          minHeight: 40,
          px: 1,
          ml: -1,
          bgcolor: 'transparent',
          '&:hover': { bgcolor: FEEDBACK_TINT.bg },
        }}
      >
        {open ? 'Hide feedback' : 'View feedback'}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 1 }}>
          {isLoading ? (
            <SubmissionFeedbackSkeleton />
          ) : isError || !feedback ? (
            <Typography variant="body2" color="text.secondary">
              The feedback could not be loaded. Please try again later.
            </Typography>
          ) : (
            <FeedbackNote feedback={feedback} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
